import React, { useState } from 'react';
import useGameStore from '../../store/gameStore';
import { useTranslation } from '../../i18n';
import { formatMoney } from '../../utils/format';
import MarkdownLite from '../MarkdownLite';
import {
  Scale, ShieldAlert, ShieldCheck, ShieldX, X, AlertTriangle,
  TrendingDown, TrendingUp, ChevronRight, FileSearch,
} from 'lucide-react';

/**
 * Fiscal audit modal — appears when a URSSAF or Customs control is triggered.
 * The player can only Cooperate (audits cannot be skipped in real life either).
 * On click, the audit resolves with a compliance score that depends on the
 * current state (insurance, certifications, support staff, active resolution).
 */
const FiscalAuditModal = () => {
  const { t } = useTranslation();
  const { pendingAudit, resolveAudit, gameStarted } = useGameStore();
  const [resolved, setResolved] = useState(null);

  if (!gameStarted || (!pendingAudit && !resolved)) return null;

  const handleResolve = () => {
    const r = resolveAudit();
    if (r && r.ok) setResolved(r);
  };

  // Phase 1: show the audit notice and the "Coopérer" CTA.
  if (pendingAudit && !resolved) {
    const isUrssaf = pendingAudit.type === 'urssaf';
    return (
      <div
        className="fixed inset-0 z-[92] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
        data-testid="fiscal-audit-modal"
      >
        <div className="bg-ink-800 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
          <div className={`px-5 py-4 border-b ${isUrssaf ? 'bg-amber-900/30 border-amber-700/40' : 'bg-sky-900/30 border-sky-700/40'}`}>
            <div className="flex items-center gap-3 text-foreground">
              <div className={`p-2 rounded-lg ${isUrssaf ? 'bg-amber-500/20' : 'bg-sky-500/20'}`}>
                <Scale className={`w-5 h-5 ${isUrssaf ? 'text-amber-300' : 'text-sky-300'}`} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-slate-300 font-mono">{t('fiscal_audit_admin_control')}</p>
                <h3 className="font-bold text-lg">{pendingAudit.title}</h3>
              </div>
            </div>
          </div>
          <div className="p-5 space-y-3">
            <p className="text-sm text-slate-300 leading-relaxed">{pendingAudit.reason}</p>
            <div className="bg-ink-700/30 rounded-md p-3 text-xs space-y-1">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">{t('fiscal_audit_items_examined')}</p>
              {isUrssaf ? (
                <ul className="text-slate-300 space-y-0.5">
                  <li className="flex items-center gap-1.5"><ChevronRight className="w-3 h-3" /> {t('fiscal_audit_urssaf_item_1')}</li>
                  <li className="flex items-center gap-1.5"><ChevronRight className="w-3 h-3" /> {t('fiscal_audit_urssaf_item_2')}</li>
                  <li className="flex items-center gap-1.5"><ChevronRight className="w-3 h-3" /> {t('fiscal_audit_urssaf_item_3')}</li>
                </ul>
              ) : (
                <ul className="text-slate-300 space-y-0.5">
                  <li className="flex items-center gap-1.5"><ChevronRight className="w-3 h-3" /> {t('fiscal_audit_customs_item_1')}</li>
                  <li className="flex items-center gap-1.5"><ChevronRight className="w-3 h-3" /> {t('fiscal_audit_customs_item_2')}</li>
                  <li className="flex items-center gap-1.5"><ChevronRight className="w-3 h-3" /> {t('fiscal_audit_customs_item_3')}</li>
                </ul>
              )}
            </div>
            <div className="flex items-start gap-2 bg-warn/10 border border-warn/30 rounded-md p-2.5 text-[11px]">
              <AlertTriangle className="w-3.5 h-3.5 text-warn flex-shrink-0 mt-0.5" />
              <p className="text-slate-300 leading-snug">
                <MarkdownLite as="span" text={t('fiscal_audit_compliance_tip')} />
              </p>
            </div>
          </div>
          <div className="px-5 py-3 border-t border-ink-600 bg-ink-700/30 flex justify-end">
            <button
              onClick={handleResolve}
              className="bg-accent hover:bg-accent/90 text-white text-sm font-bold px-5 py-2 rounded-lg flex items-center gap-2 transition-colors"
              data-testid="cooperate-audit-btn"
            >
              <FileSearch className="w-4 h-4" /> {t('fiscal_audit_cooperate_btn')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Phase 2: show the resolution outcome.
  const isClean = resolved.outcome === 'clean';
  const isMinor = resolved.outcome === 'minor';
  const isMajor = resolved.outcome === 'major';
  const Icon = isClean ? ShieldCheck : isMajor ? ShieldX : ShieldAlert;
  const accent = isClean ? 'emerald' : isMajor ? 'rose' : 'amber';

  return (
    <div
      className="fixed inset-0 z-[92] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="fiscal-audit-result"
    >
      <div className="bg-ink-800 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
        <div className={`px-5 py-4 border-b ${
          isClean ? 'bg-emerald-900/30 border-emerald-700/40'
          : isMajor ? 'bg-rose-900/30 border-rose-700/40'
          : 'bg-amber-900/30 border-amber-700/40'
        }`}>
          <div className="flex items-center justify-between text-foreground">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                isClean ? 'bg-emerald-500/20' : isMajor ? 'bg-rose-500/20' : 'bg-amber-500/20'
              }`}>
                <Icon className={`w-5 h-5 text-${accent}-300`} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-slate-300 font-mono">{t('fiscal_audit_result_label')}</p>
                <h3 className="font-bold text-lg">
                  {isClean ? t('fiscal_audit_outcome_clean') : isMinor ? t('fiscal_audit_outcome_minor') : t('fiscal_audit_outcome_major')}
                </h3>
              </div>
            </div>
            <button aria-label="Fermer"
              onClick={() => setResolved(null)}
              className="text-white/60 hover:text-white p-1"
              data-testid="close-audit-result"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="p-5 space-y-3">
          {/* Compliance score */}
          <div className="bg-ink-700/30 rounded-md p-3">
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">{t('fiscal_audit_compliance_score')}</p>
              <p className={`text-base font-black font-mono text-${accent}-400`} data-testid="audit-compliance-score">
                {Math.round(resolved.compliance)}/100
              </p>
            </div>
            <div className="h-2 rounded-full bg-ink-700/60 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${isClean ? 'bg-emerald-500' : isMajor ? 'bg-rose-500' : 'bg-amber-500'}`}
                style={{ width: `${Math.max(2, Math.min(100, resolved.compliance))}%` }}
              />
            </div>
          </div>

          {/* Outcome details */}
          {isClean ? (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-md p-3 text-xs">
              <p className="font-bold text-emerald-300 mb-1 flex items-center gap-1">
                <TrendingUp className="w-3.5 h-3.5" /> {t('fiscal_audit_no_redress')}
              </p>
              <p className="text-slate-300"><MarkdownLite as="span" text={t('fiscal_audit_clean_body')} /></p>
            </div>
          ) : (
            <div className={`bg-${accent}-500/10 border border-${accent}-500/30 rounded-md p-3 text-xs space-y-1`}>
              <p className={`font-bold text-${accent}-300 flex items-center gap-1`}>
                <TrendingDown className="w-3.5 h-3.5" /> {t('fiscal_audit_financial_penalty')}
              </p>
              <p className="text-slate-300">
                {t('fiscal_audit_redress_label')}: <strong className={`text-${accent}-300 font-mono`} data-testid="audit-fine-amount">−{formatMoney(resolved.fine)}</strong>
                {resolved.reputationDelta !== 0 && (
                  <> · {t('fiscal_audit_reputation_label')} <strong className={`text-${accent}-300`}>{resolved.reputationDelta >= 0 ? '+' : ''}{resolved.reputationDelta}</strong></>
                )}
              </p>
              <p className="text-[10px] text-slate-500 italic mt-1">
                {t('fiscal_audit_strengthen_tip')}
              </p>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-ink-600 bg-ink-700/30 flex justify-end">
          <button
            onClick={() => setResolved(null)}
            className="bg-slate-700 hover:bg-slate-600 text-white text-sm font-bold px-5 py-2 rounded-lg"
            data-testid="dismiss-audit-result-btn"
          >
            {t('close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FiscalAuditModal;
